import { withGitpodSize } from "@/assets/icons/utils";
import { Button } from "@/components/flexkit/Button";
import { ErrorMessage } from "@/components/ErrorMessage";
import { cn } from "@/components/podkit/lib/cn";
import { Tabs, TabsList, TabsTrigger } from "@/components/flexkit/tabs/Tabs";
import { Text } from "@/components/podkit/typography/Text";
import { useEnvironment, useEnvironmentGitChanges, type PlainEnvironment } from "@/queries/environment-queries";
import { FileChangeList, FileChangeListSkeleton } from "@/routes/environments/code-changes/FileChangeList";
import { EnvironmentDetailsNotFound } from "@/routes/environments/details/EnvironmentDetailsPage";
import IconChevronLeft from "central-icons/IconChevronLeft";
import IconExposure1 from "central-icons/IconExposure1";
import IconFiles from "central-icons/IconFiles";
import IconArrowRotateLeftRight from "central-icons/IconArrowRotateLeftRight";
import IconTriangleExclamation from "central-icons/IconTriangleExclamation";
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useGoBack } from "@/hooks/use-go-back";
import { type EnvironmentGitStatus, EnvironmentPhase, FileChange } from "gitpod-next-api/gitpod/v1/environment_pb";
import type { PlainMessage } from "@bufbuild/protobuf";
import IconFileText from "central-icons/IconFileText";
import IconFolder from "central-icons/IconFolder2";
import IconFolderOpen from "central-icons/IconFolderOpen";
import { OnaLettermark } from "@/assets/icons/OnaLettermark";
import { showStart } from "@/routes/environments/phase";
import { useStartEnvironment } from "@/queries/environment-queries";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { effectiveState } from "@/routes/environments/phase";

enum CodeChangesTab {
    Changes = "changes",
    Files = "files",
}

const IconChevronLeftWithSize = withGitpodSize(IconChevronLeft);
const IconFileTextWithSize = withGitpodSize(IconFileText);
const IconExposure1WithSize = withGitpodSize(IconExposure1);
const IconFilesWithSize = withGitpodSize(IconFiles);
const IconArrowRotateLeftRightWithSize = withGitpodSize(IconArrowRotateLeftRight);
const IconFolderWithSize = withGitpodSize(IconFolder);
const IconFolderOpenWithSize = withGitpodSize(IconFolderOpen);

const activeTabAtom = atom(CodeChangesTab.Changes);
const openedFileChangesAtom = atom<string[]>([]);

export const EnvironmentCodeChanges = () => {
    const { environmentId } = useParams();

    const { data: environment, isPending } = useEnvironment(environmentId);

    if (isPending) {
        return (
            <NavigationChrome environmentId={environmentId ?? ""} loading>
                <FileChangeListSkeleton className="px-2 pb-2" />
            </NavigationChrome>
        );
    }

    if (!environment) {
        return <EnvironmentDetailsNotFound />;
    }

    return (
        <NavigationChrome environmentId={environment.id} loading={false}>
            <Content environment={environment} />
        </NavigationChrome>
    );
};

const NavigationTab = ({ disabled }: { disabled: boolean }) => {
    const [activeTab, setActiveTab] = useAtom(activeTabAtom);
    return (
        <Tabs
            className="w-full max-w-screen-lg py-2"
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as CodeChangesTab)}
        >
            <TabsList className="flex-row">
                <TabsTrigger
                    LeadingIcon={IconExposure1WithSize}
                    className="flex-1 flex-row justify-center text-center"
                    value={CodeChangesTab.Changes}
                    disabled={disabled}
                >
                    Changes
                </TabsTrigger>
                <TabsTrigger
                    LeadingIcon={IconFilesWithSize}
                    className="flex-1 flex-row justify-center text-center"
                    value={CodeChangesTab.Files}
                    disabled={disabled}
                >
                    Files
                </TabsTrigger>
            </TabsList>
        </Tabs>
    );
};

const NavigationChrome = ({
    environmentId,
    loading,
    children,
}: {
    environmentId: string;
    loading: boolean;
    children: React.ReactNode;
}) => {
    const goBack = useGoBack(`/details/${environmentId}`);

    return (
        <div className="flex h-full w-full flex-col">
            <nav className="flex w-full flex-col items-center px-2 pb-2 pt-4">
                <div className="flex w-full items-center gap-2">
                    <Button
                        variant="ghost"
                        className="size-8 p-0"
                        onClick={goBack}
                        LeadingIcon={IconChevronLeftWithSize}
                        data-tracking-id="code-changes-page-goback"
                    >
                        <span className="sr-only">Go back to environment details</span>
                    </Button>
                    <header className="flex-1 select-none text-center font-medium">Code changes</header>
                    <Button disabled variant="ghost" className="invisible size-8 p-0">
                        <IconChevronLeft />
                    </Button>
                </div>
                <NavigationTab disabled={loading} />
            </nav>
            {children}
        </div>
    );
};

const ContentContainer = ({ children }: { children: React.ReactNode }) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const mainRef = useRef<HTMLElement>(null);

    useEffect(() => {
        function handleScroll(this: HTMLDivElement) {
            setIsScrolled(this.scrollTop > 0);
        }
        if (mainRef.current) {
            const main = mainRef.current;
            main.addEventListener("scroll", handleScroll);
            return () => {
                main.removeEventListener("scroll", handleScroll);
            };
        }
    }, [mainRef]);

    return (
        <main
            ref={mainRef}
            className={cn("h-full overflow-auto border-t border-transparent transition-border", {
                "border-border-strong": isScrolled,
            })}
        >
            {children}
        </main>
    );
};

// MARK: tab content

const Content = ({ environment }: { environment: PlainEnvironment }) => {
    const { data: gitChanges, error, isPending, refetch } = useEnvironmentGitChanges(environment.id);
    const activeTab = useAtomValue(activeTabAtom);
    const location = useLocation();
    const setActiveTab = useSetAtom(activeTabAtom);
    const setOpenedFileChanges = useSetAtom(openedFileChangesAtom);

    useEffect(() => {
        if (location.hash) {
            setActiveTab(CodeChangesTab.Changes);
            setOpenedFileChanges([location.hash.slice(1)]);
        }
    }, [location.hash, setActiveTab, setOpenedFileChanges]);

    const shouldShowStartEnv = showStart(environment);
    const effectiveEnvState = useMemo(() => effectiveState(environment), [environment]);

    if (shouldShowStartEnv) {
        return <EnvironmentStoppedMessasge environment={environment} />;
    }

    if (
        effectiveEnvState.state === EnvironmentPhase.STARTING ||
        effectiveEnvState.state === EnvironmentPhase.CREATING ||
        effectiveEnvState.state === EnvironmentPhase.UPDATING ||
        (effectiveEnvState.state === EnvironmentPhase.STOPPING && effectiveEnvState.resuming)
    ) {
        return <EnvironmentStartingMessasge />;
    }

    if (isPending) {
        return <FileChangeListSkeleton className="px-2 pb-2" />;
    }

    if (error || !gitChanges?.status) {
        return (
            <div className="flex h-full flex-col items-center justify-center px-2">
                <IconTriangleExclamation className="mb-2 size-10" />
                <Text className="text-center text-lg font-bold">Failed to load code changes</Text>
                <ErrorMessage className="text-center text-content-secondary" error={error} />
                <Button
                    size="sm"
                    LeadingIcon={IconArrowRotateLeftRightWithSize}
                    className="mt-4"
                    variant="outline"
                    onClick={() => refetch()}
                    data-tracking-id="retry"
                >
                    Retry
                </Button>
            </div>
        );
    }

    switch (activeTab) {
        case CodeChangesTab.Changes:
            return <FileChangesTabContent gitStatus={gitChanges.status} environment={environment} />;
        case CodeChangesTab.Files:
            return <FileTreeTabContent gitStatus={gitChanges.status} />;
    }
};

// MARK: env status msg

const EnvironmentStoppedMessasge = ({ environment }: { environment: PlainEnvironment }) => {
    const { mutate: startEnvironment, isPending } = useStartEnvironment();

    const onClick = () => {
        startEnvironment({ environmentId: environment.id });
    };

    return (
        <div className="flex h-full flex-col items-center justify-center px-2">
            <OnaLettermark className="mb-2 size-10" />
            <Text className="text-center text-lg font-bold">Environment is stopped</Text>
            <Text className="text-center text-content-secondary">Start the environment to see code changes.</Text>
            <Button
                size="sm"
                className="mt-4"
                variant="outline"
                data-tracking-id="start-environment-code-changes-page"
                onClick={onClick}
                loading={isPending}
            >
                Start environment
            </Button>
        </div>
    );
};

const EnvironmentStartingMessasge = () => {
    return (
        <div className="flex h-full items-center justify-center space-x-2 px-2">
            <IconSpinner className="animate-spin" size="lg" />
            <Text className="text-center">Starting environment…</Text>
        </div>
    );
};

// MARK: file changes tab

const FileChangesTabContent = ({
    gitStatus,
    environment,
}: {
    gitStatus: EnvironmentGitStatus;
    environment: PlainEnvironment;
}) => {
    const [openedFileChanges, setOpenedFileChanges] = useAtom(openedFileChangesAtom);
    const navigate = useNavigate();
    const { mutate: startEnvironment } = useStartEnvironment();
    const shouldStartEnvironment = showStart(environment);

    const onOpenedFilesChange = (openedFiles: string[]) => {
        if (shouldStartEnvironment) {
            startEnvironment({ environmentId: environment.id });
        }
        setOpenedFileChanges(openedFiles);
        navigate({
            hash: "",
        });
    };

    return (
        <ContentContainer>
            {gitStatus.changedFiles?.length > 0 ? (
                <FileChangeList
                    ref={(ref) => {
                        if (ref) {
                            ref.scrollToFile(location.hash.slice(1));
                        }
                    }}
                    gitStatus={gitStatus}
                    className="px-2 pb-2"
                    environment={environment}
                    openedFiles={openedFileChanges}
                    onOpenedFilesChange={onOpenedFilesChange}
                />
            ) : (
                <div className="flex h-full flex-col items-center justify-center px-2">
                    <Text className="text-center text-lg font-bold">No changed files</Text>
                    <Text className="text-center text-content-secondary">
                        Start making changes to your repository to see them here.
                    </Text>
                </div>
            )}
        </ContentContainer>
    );
};

// MARK: file tree

type FileTree = {
    [path: string]: FileTree | PlainMessage<FileChange>;
};

const FileTreeTabContent = ({ gitStatus }: { gitStatus: EnvironmentGitStatus }) => {
    const tree = useMemo(() => {
        const files = gitStatus.changedFiles;
        if (files.length === 0) {
            return null;
        }

        const commonPathPrefixIndex = (a: string, b: string) => {
            let i = 0;
            let lastSepIndex = 0;
            while (i < a.length && i < b.length && a[i] === b[i]) {
                if (a[i] === "/") {
                    lastSepIndex = i;
                }
                i += 1;
            }
            return lastSepIndex;
        };

        const lastSepIndex = (path: string) => {
            for (let i = path.length - 1; i >= 0; i -= 1) {
                if (path[i] === "/") {
                    return i;
                }
            }
            return -1;
        };

        // build a compressed trie ie a radix tree from the list of file paths
        // where prefixes are common directories of files
        // each file is keyed by its path relative to the common prefix
        // as a nested object where the key is the remaining path and the value is the file

        const radixTree: FileTree = {};

        for (const file of files) {
            let path = file.path;
            let node = radixTree;
            while (path.length > 0) {
                // find an edge that is either a prefix of the current path or one that shares a common prefix with the current path
                const edges = Object.keys(node);
                let edgeFound = false;
                for (const edge of edges) {
                    const prefixIndex = commonPathPrefixIndex(path, edge);
                    // the edge is not a prefix of the current path
                    // or does not share a common prefix
                    if (prefixIndex === 0) {
                        continue;
                    }
                    const prefixLength = prefixIndex + 1;
                    if (prefixLength === edge.length) {
                        edgeFound = true;
                        // the common prefix between path and edge is the edge itself
                        // so we traverse over the edge
                        node = node[edge] as FileTree;
                        path = path.slice(prefixIndex + 1);
                    } else {
                        // the common prefix is a prefix of both
                        // e.g src/app/dir/a.ts, src/app/c.ts
                        // and edge is src/app/
                        edgeFound = true;
                        const prefix = path.slice(0, prefixLength);
                        node[prefix] = {
                            // trim the prefix off
                            [edge.slice(prefixLength)]: node[edge],
                            [path.slice(prefixLength)]: file,
                        };
                        delete node[edge];
                        path = "";
                        break;
                    }
                }
                if (!edgeFound) {
                    const sep = lastSepIndex(path);
                    if (sep < 0) {
                        node[path] = file;
                    } else {
                        node[path.slice(0, sep + 1)] = {
                            [path.slice(sep + 1)]: file,
                        };
                    }
                    break;
                }
            }
        }

        return radixTree;
    }, [gitStatus.changedFiles]);

    if (!tree) {
        return (
            <ContentContainer>
                <div className="flex h-full flex-col items-center justify-center px-2">
                    <Text className="text-center text-lg font-bold">No changed files</Text>
                    <Text className="text-center text-content-secondary">
                        Start making changes to your repository to see them here.
                    </Text>
                </div>
            </ContentContainer>
        );
    }

    return (
        <ContentContainer>
            <div className="px-2 pb-2">
                <FileTreeView tree={tree} />
            </div>
        </ContentContainer>
    );
};

const FileTreeView = ({
    pathPrefix = "",
    tree,
    indent = 0,
}: {
    pathPrefix?: string;
    tree: FileTree;
    indent?: number;
}) => {
    const sortedEntries = useMemo(
        () => Object.entries(tree).sort((a) => (a[1] instanceof FileChange ? 1 : -1)),
        [tree],
    );

    return (
        <ul className={cn({ "pl-4": indent > 0 })}>
            {sortedEntries.map(([path, fileOrTree]) =>
                fileOrTree instanceof FileChange ? (
                    <li key={path}>
                        <Button
                            size="sm"
                            LeadingIcon={IconFileTextWithSize}
                            variant="ghost"
                            className="h-auto min-h-8 w-full justify-start rounded p-1 text-base hover:bg-surface-hover"
                        >
                            <Link className="w-full text-wrap text-start" replace to={{ hash: fileOrTree.path }}>
                                {pathPrefix + path}
                            </Link>
                        </Button>
                    </li>
                ) : (
                    <FileTreeViewDirItem
                        key={path}
                        pathPrefix={pathPrefix}
                        path={path}
                        tree={fileOrTree as FileTree}
                        indent={indent + 1}
                    />
                ),
            )}
        </ul>
    );
};

const FileTreeViewDirItem = ({
    pathPrefix,
    path,
    tree,
    indent,
}: {
    pathPrefix: string;
    path: string;
    tree: FileTree;
    indent: number;
}) => {
    const location = useLocation();
    const fullPath = pathPrefix + path;
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        setIsExpanded(location.hash.slice(1).startsWith(fullPath));
    }, [fullPath, location.hash]);

    const LeadingIcon = isExpanded ? IconFolderOpenWithSize : IconFolderWithSize;

    return (
        <li>
            <Button
                size="sm"
                variant="ghost"
                className="h-auto min-h-8 w-full justify-start text-wrap rounded p-1 hover:bg-surface-hover"
                LeadingIcon={(p) => <LeadingIcon {...p} className="shrink-0" />}
                onClick={() => setIsExpanded(!isExpanded)}
                data-tracking-id-none
            >
                <span className="text-wrap text-start">{pathPrefix + path}</span>
            </Button>
            {isExpanded && <FileTreeView tree={tree} indent={indent + 1} />}
        </li>
    );
};
